import type { ManagerDealListItem } from "@/api/client";

/** SLA warning window — deals nearing auto-approval surface in the Your
 * Feed sidebar within this many hours of their deadline. */
const SLA_WARNING_WINDOW_HOURS = 24;

/** Whether a registration is near enough to auto-approval to flag in the feed.
 *
 * Filters PENDING_REVIEW deals within the warning window — registrations
 * auto-approve at their SLA, so the feed surfaces them as the deadline nears.
 * This is the behaviour CHP-232 confirms already works for registrations; the
 * bug it fixes is that extension + closed-win review items never reached the
 * feed at all (see `managerFeedKind`).
 *
 * If severity tiers or business-hours-aware thresholds move server-side,
 * expose a `sla_status: "ok" | "warning" | "breached"` enum on
 * ManagerDealListItem and the call site becomes a one-line check
 * (`item.sla_status === "warning"`); no other component changes. */
export function isSlaWarning(item: ManagerDealListItem, now: Date): boolean {
  if (item.deal_status !== "pending_review") return false;
  if (!item.effective_sla_deadline) return false;
  const deadlineMs = Date.parse(item.effective_sla_deadline);
  if (Number.isNaN(deadlineMs)) return false;
  const hoursRemaining = (deadlineMs - now.getTime()) / 36e5;
  return hoursRemaining > 0 && hoursRemaining <= SLA_WARNING_WINDOW_HOURS;
}

/** "Time Protection Triggered" alerts surface for this many days after an
 * auto-approval — long enough to catch a reviewer back from leave, short
 * enough that the feed doesn't carry stale breaches forever. */
const BREACH_RECENCY_WINDOW_DAYS = 7;

/** Whether a LIVE deal is a recent SLA-breach auto-approval ("Time
 * Protection Triggered", deals-gap wave 2): the sweep approved it past its
 * 24h deadline (sla_breached && auto_approved) and the decision happened
 * within the recency window. The sweep records no reviewer but stamps a
 * stable `auto_approved_at` (CHP-808), so the decision time is
 * `auto_approved_at` — falling back to `reviewed_at ?? updated_at` only for
 * older rows serialised before that field existed. Keying off the stable
 * timestamp means an unrelated later mutation (hold/snooze/expiry-context),
 * which moves `updated_at`, no longer re-opens the breach window. Returns
 * false when no usable timestamp is present. */
export function isRecentTimeProtectionBreach(item: ManagerDealListItem, now: Date): boolean {
  if (item.deal_status !== "live") return false;
  if (!item.sla_breached || !item.auto_approved) return false;
  const decidedAt = item.auto_approved_at ?? item.reviewed_at ?? item.updated_at;
  if (!decidedAt) return false;
  const decidedMs = Date.parse(decidedAt);
  if (Number.isNaN(decidedMs)) return false;
  return now.getTime() - decidedMs <= BREACH_RECENCY_WINDOW_DAYS * 864e5;
}

/** "Deal closed-lost" informational alerts surface for this many days after
 * the deal was closed — long enough for a CM to notice a partner pulling
 * out, short enough that the feed doesn't carry stale closures forever.
 * Mirrors the breach window. */
const CLOSED_LOST_RECENCY_WINDOW_DAYS = 7;

/** Whether a CLOSED_LOST deal is recent enough to surface as an
 * informational feed card (CHP-336). Fires for both close paths — an
 * in-deal status change and the request-extension "close/let expire" flow —
 * since both run close_deal, which stamps `closed_at`. Returns false when
 * `closed_at` is missing (older rows before the field was serialised). */
export function isRecentClosedLost(item: ManagerDealListItem, now: Date): boolean {
  if (item.deal_status !== "closed_lost") return false;
  if (!item.closed_at) return false;
  const closedMs = Date.parse(item.closed_at);
  if (Number.isNaN(closedMs)) return false;
  return now.getTime() - closedMs <= CLOSED_LOST_RECENCY_WINDOW_DAYS * 864e5;
}

/** Deals surface a "Deal expiry notice" feed card once they're within this many
 * days of their target close date (CHP-394) — the manager's prompt to reach out
 * to the deal owner before the registration lapses. */
const EXPIRY_NOTICE_WINDOW_DAYS = 14;

/** Whole days from `now` until a target close date (date-only, local-midnight
 * boundary so a same-day deadline reads as 0, not a fractional negative).
 *
 * Both operands are pinned to LOCAL midnight to avoid a TZ off-by-one: the
 * `YYYY-MM-DD` string is parsed via local-date components (a bare `new Date(str)`
 * would read it as UTC midnight and drift against a local-midnight `now`). */
export function daysUntilExpiry(targetCloseDate: string, now: Date): number {
  const [y, m, d] = targetCloseDate.split("-").map(Number);
  if (!y || !m || !d) return NaN;
  const target = new Date(y, m - 1, d).setHours(0, 0, 0, 0);
  const today = new Date(now).setHours(0, 0, 0, 0);
  return Math.round((target - today) / 864e5);
}

/** Whether a LIVE deal should surface a "Deal expiry notice" card (CHP-394).
 *
 * Fires when the registration is within the 14-day window of its target close
 * date AND a still-future "Remind me in 7 days" snooze isn't suppressing it.
 * A snooze stamps `expiry_notice_snoozed_until` 7 days out, hiding the 14-day
 * card; once that passes the deal re-surfaces (now inside the 7-day window) as
 * the snooze-less variant. Returns false outside the window or while snoozed. */
export function isExpiryNotice(item: ManagerDealListItem, now: Date): boolean {
  if (item.deal_status !== "live") return false;
  if (!item.target_close_date) return false;
  const snoozedUntil = item.expiry_notice_snoozed_until;
  if (snoozedUntil) {
    const snoozeMs = Date.parse(snoozedUntil);
    if (!Number.isNaN(snoozeMs) && snoozeMs > now.getTime()) return false;
  }
  const days = daysUntilExpiry(item.target_close_date, now);
  return days > 0 && days <= EXPIRY_NOTICE_WINDOW_DAYS;
}

/** Whether an expiry-notice card offers the "Remind me in 7 days" snooze — only
 * the first (un-snoozed) "14-day variant" does. Once snoozed, the re-surfaced
 * 7-day card drops the button (the manager already deferred once). */
export function expiryNoticeCanSnooze(item: ManagerDealListItem): boolean {
  return !item.expiry_notice_snoozed_until;
}

/** The card variants of the manager to-do feed: the three review items
 * (CHP-232), the deals-gap wave 2 "Time Protection Triggered" breach alert,
 * the CHP-336 "Deal closed-lost" informational alert, and the CHP-394 "Deal
 * expiry notice" prompt. */
export type ManagerFeedItemKind =
  "registration" | "extension" | "win" | "breach" | "closed_lost" | "expiry_notice";

export interface ManagerFeedItem {
  deal: ManagerDealListItem;
  kind: ManagerFeedItemKind;
}

/** Classify a ledger row for the manager to-do feed, or null if it isn't one.
 *
 *   - **registration** (PENDING_REVIEW) — gated by the SLA-warning window
 *     (`isSlaWarning`); registrations auto-approve, so they surface as the
 *     deadline nears — the existing behaviour CHP-232 leaves intact.
 *   - **extension** (EXTENSION_PENDING) — *every* pending extension request
 *     appears (CHP-232: "all deal extension requests appear as a card"); it
 *     carries a 48h extension-SLA countdown in `effective_sla_deadline`.
 *   - **win** (WIN_PENDING_REVIEW) — *every* closed-win submission appears.
 *     Wins do NOT auto-approve, but since CHP-1176 they carry a 48h review
 *     SLA: `effective_sla_deadline` holds `win_sla_deadline`, and a breach is
 *     flag-only (`deal.win_sla_breach` — the deal stays queued). The card
 *     therefore shows the neutral review-SLA countdown, never the
 *     registration card's "Auto-approves in…" copy.
 *   - **breach** (LIVE, recently auto-approved past its SLA) — the
 *     high-priority "Time Protection Triggered" alert (deals-gap wave 2);
 *     see `isRecentTimeProtectionBreach`.
 *   - **closed_lost** (CLOSED_LOST, recently closed) — the CHP-336
 *     informational alert; see `isRecentClosedLost`. */
export function managerFeedKind(item: ManagerDealListItem, now: Date): ManagerFeedItemKind | null {
  // CHP-369/371/375: a dismissed card (the ✕, or viewing a closed-lost) drops
  // off the feed for the whole manager pool.
  if (item.manager_feed_dismissed_at) return null;
  switch (item.deal_status) {
    case "pending_review":
      return isSlaWarning(item, now) ? "registration" : null;
    case "extension_pending":
      return "extension";
    case "win_pending_review":
      return "win";
    case "live":
      // A recent SLA-breach auto-approval (breach) outranks the routine expiry
      // prompt; only one card per deal.
      if (isRecentTimeProtectionBreach(item, now)) return "breach";
      return isExpiryNotice(item, now) ? "expiry_notice" : null;
    case "closed_lost":
      return isRecentClosedLost(item, now) ? "closed_lost" : null;
    default:
      return null;
  }
}

/** Build the manager to-do feed from the unified ledger dataset (CHP-232). */
export function buildManagerFeed(deals: ManagerDealListItem[], now: Date): ManagerFeedItem[] {
  return deals.flatMap((deal) => {
    const kind = managerFeedKind(deal, now);
    return kind ? [{ deal, kind }] : [];
  });
}

/** Compact "auto-approves in 5h" / "12m" / "due now" string. */
export function formatSlaCountdown(deadline: string, now: Date): string {
  const deadlineMs = Date.parse(deadline);
  if (Number.isNaN(deadlineMs)) return "—";
  const diffMs = deadlineMs - now.getTime();
  if (diffMs <= 0) return "Auto-approves now";
  const totalMinutes = Math.floor(diffMs / 60000);
  if (totalMinutes < 60) return `Auto-approves in ${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes - hours * 60;
  if (minutes === 0) return `Auto-approves in ${hours}h`;
  return `Auto-approves in ${hours}h ${minutes}m`;
}

/** CHP-1176: neutral review-SLA countdown for card kinds that never
 * auto-finalise (the win card). Same compact shape as formatSlaCountdown but
 * without the "Auto-approves" framing, which would be factually wrong here. */
export function formatReviewSlaCountdown(deadline: string, now: Date): string {
  const deadlineMs = Date.parse(deadline);
  if (Number.isNaN(deadlineMs)) return "—";
  const diffMs = deadlineMs - now.getTime();
  if (diffMs <= 0) return "Review SLA breached";
  const totalMinutes = Math.floor(diffMs / 60000);
  if (totalMinutes < 60) return `Review SLA: ${totalMinutes}m remaining`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes - hours * 60;
  if (minutes === 0) return `Review SLA: ${hours}h remaining`;
  return `Review SLA: ${hours}h ${minutes}m remaining`;
}
